import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from './Icon';
import s from './Combo.module.css';

// A type-ahead field (the suggestion-list standard: orange row under the cursor / arrow keys, black text).
// options: [{ id, label, icon?, color?, sub? }]; value = id (or null); onChange(option | null).
// `free` → what's typed can be kept as is (onChange({ id: null, label: typed })) — e.g. a customer not in the books yet.
export default function Combo({ options, value, onChange, placeholder = 'Search…', icon = 'search', free = false, freeLabel = 'Use', autoFocus = false, emptyText = 'Nothing matches.' }) {
  const sel = options.find((o) => o.id === value) || null;
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const wrap = useRef(null);
  const input = useRef(null);
  const list = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return options.filter((o) => !ql || `${o.label} ${o.sub || ''}`.toLowerCase().includes(ql)).slice(0, 50);
  }, [options, q]);
  const rows = free && q.trim() && !list.some((o) => o.label.toLowerCase() === q.trim().toLowerCase()) ? [...list, { id: null, label: q.trim(), free: true }] : list;
  useEffect(() => setI(0), [q]);
  useEffect(() => {
    if (!open) return undefined;
    const out = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', out);
    return () => document.removeEventListener('mousedown', out);
  }, [open]);
  const pick = (o) => { onChange(o); setQ(''); setOpen(false); };
  return (
    <div className={s.wrap} ref={wrap}>
      <div className={s.field} data-open={open || undefined} onClick={() => { setOpen(true); input.current?.focus(); }}>
        {sel && !open ? (
          <span className={s.value}>{sel.color ? <span className="dot" style={{ background: sel.color }} /> : <Icon name={icon} size={15} />}{sel.icon ? <span>{sel.icon}</span> : null}<span className={s.vText}>{sel.label}</span></span>
        ) : <Icon name={icon} size={15} />}
        <input ref={input} className={s.input} data-hidden={sel && !open ? '' : undefined} value={q} autoFocus={autoFocus} placeholder={sel ? '' : placeholder}
          onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setI((v) => Math.min(v + 1, rows.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setI((v) => Math.max(v - 1, 0)); }
            else if (e.key === 'Enter' && open && rows[i]) { e.preventDefault(); e.stopPropagation(); pick(rows[i]); }
            else if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); }
            else if (e.key === 'Backspace' && !q && sel) onChange(null);
          }} />
        {sel ? <button type="button" className={s.clear} onClick={(e) => { e.stopPropagation(); onChange(null); }} aria-label="Clear"><Icon name="x" size={14} /></button> : <Icon name="chevron-down" size={15} />}
      </div>
      {open ? (
        <div className={s.pop} role="listbox">
          {rows.map((o, k) => (
            <button type="button" key={o.id || `free:${o.label}`} role="option" aria-selected={k === i} className={s.opt} data-on={k === i || undefined}
              onMouseEnter={() => setI(k)} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(o)}>
              {o.free ? <Icon name="plus" size={14} /> : o.color ? <span className="dot" style={{ background: o.color }} /> : null}
              {o.icon ? <span className={s.ic}>{o.icon}</span> : null}
              <span className={s.name}>{o.free ? `${freeLabel} “${o.label}”` : o.label}</span>
              {o.sub ? <span className={s.sub}>{o.sub}</span> : null}
            </button>
          ))}
          {!rows.length ? <div className={s.none}>{emptyText}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
